import React, { FC, useMemo } from 'react'
import './style.scss'

interface VerificationOption {
  value: string
  label: string
  description: string
  icon?: React.ReactNode
}

interface VerificationModeSelectorProps {
  multiple?: boolean
  value?: string | string[]
  onChange?: (newValue: string | string[] | null | any) => void
  className?: string
  options?: VerificationOption[]
}

const VerificationModeSelector: FC<VerificationModeSelectorProps> = ({
                                                                       multiple = true,
                                                                       value: controlledValue,
                                                                       onChange,
                                                                       className = '',
                                                                       options,
                                                                     }) => {
  const defaultOptions = useMemo(
    () => [
      { value: 'complete', label: 'Complète', description: 'Vérification complète (email + téléphone + nom & prénom)' },
      { value: 'email', label: 'Email seul', description: "Vérifier uniquement l'email" },
      { value: 'phone', label: 'Téléphone seul', description: "Vérifier uniquement le téléphone" },
      // { value: 'email_phone', label: 'Email + Téléphone', description: "Vérifier l'email et le téléphone" },
    ],
    []
  )

  options = options || defaultOptions
  const isControlled = controlledValue !== undefined

  const selectedSet = useMemo(() => {
    if (isControlled) {
      if (multiple) return new Set(Array.isArray(controlledValue) ? controlledValue : [controlledValue])
      return new Set(controlledValue ? [controlledValue] : [])
    }
    return new Set()
  }, [controlledValue, multiple, isControlled])

  function toggleOption(optValue: string) {
    if (isControlled) {
      if (multiple) {
        const current = new Set(Array.isArray(controlledValue) ? controlledValue : [])
        if (current.has(optValue)) current.delete(optValue)
        else current.add(optValue)
        onChange && onChange(Array.from(current))
      } else {
        const next = controlledValue === optValue ? null : optValue
        onChange && onChange(next)
      }
    }
  }

  function isSelected(optValue: string) {
    if (isControlled) return selectedSet.has(optValue)
    return false
  }

  function getHelpText(selectedValues: string[]) {
    if (!selectedValues || selectedValues.length === 0) return 'Choisissez un ou plusieurs modes de vérification.'
    if (selectedValues.includes('complete')) return 'Mode complète: exécute toutes les vérifications disponibles.'
    if (selectedValues.length === 1) {
      const v = selectedValues[0]
      if (v === 'email') return "Vérification centrée sur l'email. Rapide et léger."
      if (v === 'phone') return 'Vérification centrée sur le téléphone.'
      if (v === 'email_phone') return "Vérification de l'email et du téléphone ensemble."
    }
    return 'Combinaison personnalisée de vérifications sélectionnée.'
  }

  const selectedValues = isControlled ? Array.from(selectedSet) : []

  return (
    <div className={`VerificationModeSelector ${className}`}>
      <h4 className="VerificationModeSelector__title">Type de vérification</h4>
      <div className="VerificationModeSelector__grid">
        {options.map((opt) => {
          const selected = isSelected(opt.value)
          return (
            <button
              key={opt.value}
              type="button"
              role={multiple ? 'checkbox' : 'radio'}
              aria-checked={selected}
              onClick={() => toggleOption(opt.value)}
              className={`VerificationModeSelector__option ${selected ? 'is-selected' : ''}`}
            >
              <div className="VerificationModeSelector__icon">
                {opt.icon ?? <span className={`VerificationModeSelector__iconPlaceholder ${selected ? 'text-success' : ''}`}>
                    {selected ? '★' : 'o'}
                </span>}
              </div>
              <div className="VerificationModeSelector__content">
                <div className="VerificationModeSelector__label">{opt.label}</div>
                <div className="VerificationModeSelector__description">{opt.description}</div>
              </div>
              <input
                type={multiple ? 'checkbox' : 'radio'}
                name="verificationMode"
                value={opt.value}
                checked={selected}
                onChange={() => toggleOption(opt.value)}
                className="VerificationModeSelector__input"
              />
            </button>
          )
        })}
      </div>
      <p className="VerificationModeSelector__help text-center">{getHelpText(selectedValues as string[])}</p>
    </div>
  )
}

export default VerificationModeSelector
